import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { RfidOverviewResponse } from '../../../../../hooks';
import { Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { RotateDirection } from '../../../../../stories/atoms/icon/Icon.styles';
import { areaOverviewTranslations } from '../../../../../translations/areaOverview';
import { OverviewWidget } from '../../../../areaOverview/overviewWidget/OverviewWidget';
import { AreaWidgetType } from '../../../../areaOverview/util';
import { RfidMetricsContainer, StyledIconWithText } from './RFIDOverviewWidget.styles';

type RfidOverviewTranslationType = keyof typeof areaOverviewTranslations.rfidOverview;

type RFIDOverviewWidgetProps = {
  data: RfidOverviewResponse | null;
  isLoading: boolean;
  isError: boolean;
  errorRetryHandler: () => void;
};

export const RFIDOverviewWidget: FC<RFIDOverviewWidgetProps> = observer(
  ({ data, isLoading, isError, errorRetryHandler }) => {
    const { t } = useTranslation();

    const rfidRows = [
      {
        testId: 'negative-approval-rate',
        label: 'negativeApprovalRateText',
        icon: Icons.ARROW,
        rotate: 'left',
        value: data?.negativeApprovalRate ? `${data.negativeApprovalRate.toFixed(2)}%` : '0%',
      },
      {
        testId: 'positive-approval-rate',
        label: 'positiveApprovalRateText',
        icon: Icons.ARROW,
        rotate: 'right',
        value: data?.positiveApprovalRate ? `${data.positiveApprovalRate.toFixed(2)}%` : '0%',
      },
      {
        testId: 'rfid-sales',
        label: 'rfidSalesText',
        icon: Icons.CONVERSION,
        value: data?.rfidSales ? `${data.rfidSales.toFixed(2)}%` : '0%',
      },
      {
        testId: 'retagging-rate',
        label: 'retaggingRateText',
        icon: Icons.TAG,
        value: data?.retaggingRate ? `${data.retaggingRate.toFixed(2)}%` : '0%',
      },
      {
        testId: 'rfid-count',
        label: 'rfidCountText',
        icon: Icons.HOURLASS,
        value: data?.rfidCounts?.toFixed() ?? '0.00',
      },
      {
        testId: 'time-spent',
        label: 'timeSpentText',
        icon: Icons.CLOCK,
        value: data?.timeSpent?.toFixed(2) ?? '0.00',
      },
      {
        testId: 'devices',
        label: 'deviceUsageText',
        icon: Icons.SCAN,
        value: data?.devices?.toFixed() ?? '0.00',
      },
      {
        testId: 'stock-accuracy',
        label: 'stockAccuracyText',
        icon: Icons.DELIVERY,
        value: data?.stockAccuracy?.toFixed(2) ?? '0.00',
      },
    ];

    const getRotateDirection = (rotate?: string): RotateDirection | undefined => {
      if (rotate === 'left' || rotate === 'right' || rotate === 'up' || rotate === 'down') {
        return rotate as RotateDirection;
      }
      return undefined;
    };

    return (
      <OverviewWidget
        id={AreaWidgetType.RfidOverview}
        isLoading={isLoading}
        isError={isError}
        errorRetryHandler={errorRetryHandler}
        title={t(
          areaOverviewTranslations.rfidOverview.title.key,
          areaOverviewTranslations.rfidOverview.title.defaultValue,
        )}
        headerIcon={Icons.TAG}
        customContent={
          <ColumnFlexContainer>
            <Typography margin={[0, 0, 1]}>
              {t(
                areaOverviewTranslations.rfidOverview.widgetText.key,
                areaOverviewTranslations.rfidOverview.widgetText.defaultValue,
              )}
            </Typography>
            <RfidMetricsContainer maxHeight={[52]} overflow="scroll" data-testid="rfid-metrics-container">
              {rfidRows.map((row) => (
                <RowFlexContainer justifyContent="between" key={row.testId} padding={[2, 0, 3]}>
                  <StyledIconWithText
                    data-testid={row.testId}
                    label={t(
                      areaOverviewTranslations.rfidOverview[row.label as RfidOverviewTranslationType].key,
                      areaOverviewTranslations.rfidOverview[row.label as RfidOverviewTranslationType].defaultValue,
                    )}
                    iconOptions={{
                      icon: row.icon,
                      size: 'small',
                      rotate: getRotateDirection(row.rotate),
                    }}
                    position="left"
                  />
                  <Typography>{row.value}</Typography>
                </RowFlexContainer>
              ))}
            </RfidMetricsContainer>
          </ColumnFlexContainer>
        }
      />
    );
  },
);
